import {HeaderRowMode} from '../shared/models/export-columns.model';
import {
    ExportFileFormat,
    fieldSeparatorCharacters,
    textDelimiterCharacters,
} from '../shared/models/export-file-format.model';
import {ExportedCell, ExportedGridInput, exportedGrid, ExportedValue} from './exported-value.model';

export type FilePreviewInput = ExportedGridInput & {headerRow: HeaderRowMode};

export type FilePreviewCell = ExportedCell;

export type PreviewTokenRole =
    | 'punctuation'
    | 'separator'
    | 'text'
    | 'number'
    | 'date'
    | 'fieldName'
    | 'missing'
    | 'heading';

export type PreviewToken = {text: string; role: PreviewTokenRole};

export type PreviewLine = ReadonlyArray<PreviewToken>;

export type FilePreviewContent =
    | {kind: 'csv'; lines: ReadonlyArray<PreviewLine>}
    | {kind: 'json'; lines: ReadonlyArray<PreviewLine>}
    | {
          kind: 'excel';
          header: ReadonlyArray<string> | null;
          rows: ReadonlyArray<ReadonlyArray<FilePreviewCell>>;
      };

const punctuation = (text: string): PreviewToken => ({text, role: 'punctuation'});

function csvFieldTokens(
    value: ExportedValue,
    fileFormat: ExportFileFormat,
    role: PreviewTokenRole,
): PreviewToken[] {
    const delimiter = textDelimiterCharacters[fileFormat.textDelimiter];
    const separator = fieldSeparatorCharacters[fileFormat.fieldSeparator];
    const delimiterNecessary =
        delimiter !== null &&
        (value.text.includes(separator) ||
            value.text.includes('\n') ||
            value.text.includes(delimiter));
    if (!delimiterNecessary) {
        return [{text: value.text, role}];
    }
    return [
        punctuation(delimiter),
        {text: value.text.replaceAll(delimiter, delimiter.repeat(2)), role},
        punctuation(delimiter),
    ];
}

function csvLine(
    fields: ReadonlyArray<ExportedValue>,
    fileFormat: ExportFileFormat,
    role: (field: ExportedValue) => PreviewTokenRole,
): PreviewLine {
    const separator: PreviewToken = {
        text: fieldSeparatorCharacters[fileFormat.fieldSeparator],
        role: 'separator',
    };
    return fields.flatMap((field, index) => [
        ...(index === 0 ? [] : [separator]),
        ...csvFieldTokens(field, fileFormat, role(field)),
    ]);
}

export function buildCsvPreview(input: FilePreviewInput): FilePreviewContent {
    const {headers, rows} = exportedGrid(input);
    const dataLines = rows.map(row => csvLine(row, input.fileFormat, field => field.role));
    if (input.headerRow === 'NONE') {
        return {kind: 'csv', lines: dataLines};
    }
    return {
        kind: 'csv',
        lines: [csvLine(headers, input.fileFormat, () => 'heading'), ...dataLines],
    };
}

export function buildExcelPreview(input: FilePreviewInput): FilePreviewContent {
    const {headers, rows} = exportedGrid(input);
    return {
        kind: 'excel',
        header: input.headerRow === 'NONE' ? null : headers.map(header => header.text),
        rows: rows.map(row => row.map(value => value.cell)),
    };
}

const jsonIndent = '  ';

function jsonValueTokens(value: ExportedValue): PreviewToken[] {
    if (value.text === '') {
        return [{text: 'null', role: 'missing'}];
    }
    return [
        punctuation('"'),
        {text: escapedInJson(value.text), role: value.role},
        punctuation('"'),
    ];
}

function escapedInJson(text: string): string {
    return JSON.stringify(text).slice(1, -1);
}

function jsonFieldLine(name: string, value: ExportedValue, last: boolean): PreviewLine {
    return [
        punctuation(`${jsonIndent}${jsonIndent}"`),
        {text: escapedInJson(name), role: 'fieldName'},
        punctuation('": '),
        ...jsonValueTokens(value),
        ...(last ? [] : [punctuation(',')]),
    ];
}

export function buildJsonPreview(input: FilePreviewInput): FilePreviewContent {
    const {headers, rows} = exportedGrid(input);
    const objects = rows.map((row, rowIndex) => [
        [punctuation(`${jsonIndent}{`)],
        ...row.map((value, columnIndex) =>
            jsonFieldLine(headers[columnIndex].text, value, columnIndex === row.length - 1),
        ),
        [punctuation(`${jsonIndent}}${rowIndex === rows.length - 1 ? '' : ','}`)],
    ]);
    return {
        kind: 'json',
        lines: [[punctuation('[')], ...objects.flat(), [punctuation(']')]],
    };
}
